import { sliceWhen } from '@salescore/buff-common'
import type { TailwindCssColor } from '@salescore/client-base'
import type {
  ConditionalEffect,
  DynamicSelectOptionConfig,
  PropertyMeta,
  SelectOption,
  ViewConfigField,
  ViewQueryField,
  ViewQueryNode,
  ViewQueryRecordNode,
  ViewUiKpiPivotColumn,
  ViewUiSheetColumn,
  ViewUISheetColumnMeasureCell,
} from '@salescore/core'
import { Array, Number, pipe } from 'effect'
import type { ReactElement } from 'react'

import { defaultNodeBlock } from '../recoil/columns'
import { buildHeaderCellHeight, isKpiOrKpiPivotContext, type RSheetContext } from '../recoil/context'
import type { RSheetsNodeBlock } from '../rsheet/recoil/selectors/rSheetsNodeBlock'

export const CELL_WIDTH = 150

export type UnionOfRSheetColumn<T extends RSheetRecordNode> =
  SheetSystemColumn<T> | SheetColumn<T> | PivotSystemColumn<T> | PivotRowColumn<T> | PivotColumn<T>

export interface RSheetColumn<T extends RSheetRecordNode> {
  isSystem?: boolean // 行番号などのカラム
  field?: ViewQueryField
  node: ViewQueryNode
  // eslint-disable-next-line sonarjs/todo-tag
  nodeBlock?: RSheetsNodeBlock // TODO: 命名
  key: string
  index: number
  configIndex?: number // configのfieldsにおけるindex。ダッシュボードなら rows と kpis, KPI なら rows, シートなら fields におけるindex
  width?: number
  title?: string
  smallTitle?: string // 左上に表示する小さなタイトル文言
  helpText?: string
  nodeType?: 'root' | 'children' | 'parent' | 'child'
  color?: TailwindCssColor
  icon?: string | ReactElement
  value: (record: T) => unknown
  // eslint-disable-next-line sonarjs/todo-tag
  label?: (record: T) => unknown // TODO: optional
  highlight?: (record: T) => boolean | boolean[]
  // eslint-disable-next-line sonarjs/no-redundant-optional,sonarjs/use-type-alias
  sortState?: 'asc' | 'desc' | undefined
  type?: RSheetsColumnType
  metaType?: RSheetsColumnMeta
  selectOptions?: RSheetsSelectOption[]
  headerPopoverContent?: ReactElement
  readonly?: boolean
  deletable?: boolean
  columnDeletable?: boolean
  visible?: boolean
  isFiltered?: boolean
  isHighlighted?: boolean
  required?: boolean
  conditionalEffects?: ConditionalEffect[]
  measures?: ViewUiSheetColumn['measures']
  cellBackgroundColor?: string // 小計セルのために使用
  kpiBorderRight?: boolean
  dynamicSelectOptionConfig?: DynamicSelectOptionConfig
  configField?: ViewConfigField
}

// チェックボックス系の列 (編集モードを持たず、値はトグルで入る)。判定がズレると sync/async のキー処理連携が壊れるため一元化する
export const isCheckboxColumn = (column: RSheetColumn<RSheetRecordNode>): boolean =>
  column.type === 'checkbox' || column.type === 'rowCheckbox' || column.type === 'boolean'

export interface SheetSystemColumn<T extends ViewQueryRecordNode> {
  columnType: 'sheet_system'
  index: number
  configIndex: undefined
  isSystem: true
  node: ViewQueryNode
  key: string
  width: number
  type: 'rowIndex' | 'rowCheckbox' | 'open'
  value: (record: T) => unknown
}

export interface SheetColumn<T extends ViewQueryRecordNode> {
  columnType: 'sheet'
  index: number
  field: ViewQueryField
  node: ViewQueryNode
  nodeBlock: RSheetsNodeBlock
  key: string
  configIndex: number | undefined
  width: number
  title: string
  helpText: string | undefined
  nodeType: 'root' | 'children' | 'parent' | 'child' | undefined
  color: TailwindCssColor | undefined
  value: (record: T) => unknown
  label: ((record: T) => unknown) | undefined
  highlight: (record: T) => boolean | boolean[]
  sortState: 'asc' | 'desc' | undefined
  type: ViewUiSheetColumn['fieldType']
  metaType: ViewUiSheetColumn['fieldMetaType']
  selectOptions: RSheetsSelectOption[] | undefined
  readonly: boolean
  deletable: boolean | undefined
  columnDeletable: boolean | undefined
  visible: boolean | undefined
  isFiltered: boolean
  isHighlighted: boolean
  required: boolean
  conditionalEffects: ConditionalEffect[] | undefined
  measures: ViewUISheetColumnMeasureCell[] | undefined
  dynamicSelectOptionConfig: DynamicSelectOptionConfig | undefined
  configField: ViewConfigField | undefined
}

export interface PivotSystemColumn<T extends ViewQueryRecordNode> {
  columnType: 'pivot_system'
  index: number
  configIndex: undefined
  isSystem: true
  node: ViewQueryNode
  key: string
  width: number
  type: `rowIndex`
  value: (record: T) => unknown
}

export interface PivotRowColumn<T extends ViewQueryRecordNode> {
  columnType: 'pivot_row'
  index: number
  configIndex: number | undefined
  type: ViewUiKpiPivotColumn['fieldType'] | 'rowKpi'
  field: ViewQueryField
  metaType: RSheetsColumnMeta | undefined
  selectOptions: RSheetsSelectOption[] | undefined
  node: ViewQueryNode
  key: string
  title: string
  isFiltered: boolean
  width: number
  readonly: true
  icon: string | ReactElement | undefined
  value: (recordNode: T) => void
  label: (recordNode: T) => unknown
}

export interface PivotColumn<T extends ViewQueryRecordNode> {
  columnType: 'pivot'
  index: number
  configIndex: number | undefined
  type: 'kpi'
  field: ViewQueryField
  node: ViewQueryNode
  key: string
  title: string
  smallTitle: string | undefined // 左上に表示する小さなタイトル文言
  nodeBlock: RSheetsNodeBlock
  icon: string | ReactElement | undefined
  helpText: string | undefined
  width: number
  readonly: true
  sortState: 'asc' | 'desc' | undefined
  cellBackgroundColor: string | undefined // 小計セルのために使用
  kpiBorderRight: boolean
  value: (recordNode: T) => void
  label: (recordNode: T) => unknown
}

export type RSheetsSelectOption = SelectOption
export type RSheetsColumnType =
  | 'array'
  | 'boolean'
  | 'integer'
  | 'numeric'
  | 'object'
  | 'string'
  | 'date'
  | 'datetime'
  | 'time'
  | 'reference'
  | 'checkbox'
  | 'open'
  | 'rowIndex'
  | 'rowCheckbox'
  | 'notFound'
  | 'kpi'
  | 'rowKpi'
  | 'user'
export type RSheetsColumnMeta = PropertyMeta

export interface RSheetRecordNode {
  id: string | undefined
  attributes: Record<string, unknown>
  meta: {
    height: number
    innerRowIndexStart: number
    innerRowIndexEnd: number
    // eslint-disable-next-line sonarjs/todo-tag
    highlightAttributeNames?: string[] // TODO: 後から適切な形式を考える予定
  }
  children: RSheetTableNode[]
}

export interface RSheetTableNode {
  nodeName: string
  children: RSheetRecordNode[]
}
export interface RSheetsNodeBlockCell {
  block: RSheetsNodeBlock
  width: number
  label: string
  smallLabel: string | undefined
  color: TailwindCssColor | undefined
  tooltipText?: string
  // onClick
}

export type RSheetsNodeBlockRow = RSheetsNodeBlockCell[]

const TREE_HEADER_HEIGHT_PADDING = 0

export const getNodeBlock = (nodeBlocks: RSheetsNodeBlock[], widths: number[]): RSheetsNodeBlockRow[] => {
  if (nodeBlocks.length === 0) {
    return []
  }

  const maxDepth = Math.max(...nodeBlocks.map((x) => x.nodePath.length))
  const blockHeader = (maxDepth - 1 >= 0 ? Array.range(0, maxDepth - 1) : Array.empty<number>()).map(
    (depth): RSheetsNodeBlockRow => {
      const blockGroups = pipe(
        nodeBlocks,
        sliceWhen((a, b) => a.nodePath[depth] !== b.nodePath[depth]),
      )
      const ws = [...widths]
      return blockGroups.map((blockGroup): RSheetsNodeBlockCell => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const block = blockGroup[0]!
        const label = block.labels[depth]
        const smallLabel = (block.smallLabels ?? [])[depth]
        const color = block.colors[depth]
        const tooltipText = (block.helpTexts ?? [])[depth]
        return {
          block,
          label: label ?? '-',
          smallLabel,
          width: Number.sumAll(ws.splice(0, blockGroup.length)),
          color,
          tooltipText,
        }
      })
    },
  )

  return blockHeader
}

export const buildHeaderHeight = (columns: Array<RSheetColumn<RSheetRecordNode>>, context: RSheetContext) => {
  const nodeBlockHeaderAll = getNodeBlock(
    columns.map((x) => x.nodeBlock ?? defaultNodeBlock),
    columns.map((x) => x.width ?? 100),
  )
  const maxDepth = nodeBlockHeaderAll.length
  const treeHeaderHeightPerNode = isKpiOrKpiPivotContext(context) ? 42 : 20
  const treeHeaderHeight = maxDepth * treeHeaderHeightPerNode + TREE_HEADER_HEIGHT_PADDING
  const headerCellHeight = buildHeaderCellHeight(context)
  const headerTopMargin = 2
  const headerHeight = treeHeaderHeight + headerCellHeight - headerTopMargin
  const headerTopOffset = treeHeaderHeight - headerTopMargin

  return {
    headerHeight,
    headerTopOffset,
  }
}

export const fixedNodeBlockHeader = (
  columns: Array<RSheetColumn<RSheetRecordNode>>,
  leftFixedColumns: Array<RSheetColumn<RSheetRecordNode>>,
  notFixedColumns: Array<RSheetColumn<RSheetRecordNode>>,
): {
  leftFixedNodeBlockHeader: RSheetsNodeBlockRow[]
  notFixedNodeBlockHeader: RSheetsNodeBlockRow[]
  maxDepth: number
} => {
  const leftFixedNodeBlockHeader = getNodeBlock(
    leftFixedColumns.map((x) => x.nodeBlock ?? defaultNodeBlock),
    leftFixedColumns.map((x) => x.width ?? 100),
  )
  const notFixedNodeBlockHeader = getNodeBlock(
    notFixedColumns.map((x) => x.nodeBlock ?? defaultNodeBlock),
    notFixedColumns.map((x) => x.width ?? 100),
  )
  const nodeBlockHeaderAll = getNodeBlock(
    columns.map((x) => x.nodeBlock ?? defaultNodeBlock),
    columns.map((x) => x.width ?? 100),
  )
  const maxDepth = nodeBlockHeaderAll.length

  return {
    leftFixedNodeBlockHeader,
    notFixedNodeBlockHeader,
    maxDepth,
  }
}

export const HEADER_RIGHT_WIDTH_MARGIN = 400

export const buildColumnWidths = (columns: Array<RSheetColumn<RSheetRecordNode>>): number[] => [
  ...columns.map((x) => x.width ?? CELL_WIDTH),
  HEADER_RIGHT_WIDTH_MARGIN,
]
